import { MessageAudienceEnum, MessageAudienceLabelEnum, MessageLanguageEnum } from '@/enums';
import { APP_SCREEN_OPTIONS } from '@/constants/promotion.constant';
import type { IAdminMessageComposerProps, IAdminMessageFormState, ISelectOption } from '@/interfaces';
import { useCallback, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import UserMultiSelect from './UserMultiSelect';
import { ControlledSelect } from '@/components/controlled';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui';
import { Info, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

const USERNAME_TEMPLATE = '{{ username }}';
const ACTION_TEMPLATE = '{{ action }}';

const ComposerCard = ({ onOpenPreview, onSend }: IAdminMessageComposerProps) => {
  const { register, control, getValues, setValue, formState: { errors } } = useFormContext<IAdminMessageFormState>();
  const activeLang = useWatch({ control, name: 'activeLang' }) ?? MessageLanguageEnum.FR;
  const audience = useWatch({ control, name: 'audience' }) ?? MessageAudienceEnum.ALL;
  const messageFr = useWatch({ control, name: 'messageFr' }) ?? '';
  const messageEn = useWatch({ control, name: 'messageEn' }) ?? '';
  const messageFrError = errors.messageFr?.message;
  const messageEnError = errors.messageEn?.message;

  const hasLangError =
    activeLang === MessageLanguageEnum.FR
      ? !!(errors.messageFr || errors.cta?.text?.fr)
      : !!(errors.messageEn || errors.cta?.text?.en);

  // Refs merged with RHF so we can read cursor position
  const messageFrRef = useRef<HTMLTextAreaElement | null>(null);
  const messageEnRef = useRef<HTMLTextAreaElement | null>(null);

  const { ref: frRegisterRef, ...messageFrRegister } = register('messageFr', {
    required: 'Message FR requis',
    minLength: { value: 20, message: 'Minimum 20 caractères' }
  });
  const { ref: enRegisterRef, ...messageEnRegister } = register('messageEn', {
    required: 'Message EN requis',
    minLength: { value: 20, message: 'Minimum 20 caractères' }
  });

  const setFrRef = useCallback((el: HTMLTextAreaElement | null) => {
    messageFrRef.current = el;
    frRegisterRef(el);
  }, [frRegisterRef]);

  const setEnRef = useCallback((el: HTMLTextAreaElement | null) => {
    messageEnRef.current = el;
    enRegisterRef(el);
  }, [enRegisterRef]);

  const insertAtCursor = useCallback((
    fieldName: 'messageFr' | 'messageEn',
    ref: React.RefObject<HTMLTextAreaElement | null>,
    text: string
  ) => {
    const el = ref.current;
    const current = getValues(fieldName);
    const start = el?.selectionStart ?? current.length;
    const end = el?.selectionEnd ?? current.length;
    const next = current.slice(0, start) + text + current.slice(end);
    setValue(fieldName, next, { shouldValidate: true });
    // Restore cursor after React re-render
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(start + text.length, start + text.length);
    });
  }, [getValues, setValue]);

  const handleInsertUsername = useCallback(() => {
    if (activeLang === MessageLanguageEnum.FR) {
      insertAtCursor('messageFr', messageFrRef, USERNAME_TEMPLATE);
    } else {
      insertAtCursor('messageEn', messageEnRef, USERNAME_TEMPLATE);
    }
  }, [activeLang, insertAtCursor]);

  const handleInsertAction = useCallback(() => {
    if (activeLang === MessageLanguageEnum.FR) {
      insertAtCursor('messageFr', messageFrRef, ACTION_TEMPLATE);
    } else {
      insertAtCursor('messageEn', messageEnRef, ACTION_TEMPLATE);
    }
  }, [activeLang, insertAtCursor]);

  const audienceOptions: ISelectOption[] = [
    { title: MessageAudienceLabelEnum.ALL, value: MessageAudienceEnum.ALL },
    { title: MessageAudienceLabelEnum.ALL_IOS, value: MessageAudienceEnum.ALL_IOS },
    { title: MessageAudienceLabelEnum.ALL_ANDROID, value: MessageAudienceEnum.ALL_ANDROID },
    { title: MessageAudienceLabelEnum.SPECIFIC, value: MessageAudienceEnum.SPECIFIC },
  ];

  const inputClass = 'w-full rounded-lg border border-border bg-white px-2 py-1.5 text-xs focus:outline-hidden focus:ring-1 focus:ring-foreground placeholder:text-muted-foreground/60';

  return (
    <div className='bg-white rounded-2xl shadow-soft border border-border'>
      <div className='px-4 py-3 border-b border-border flex items-center justify-between'>
        <h2 className='font-semibold'>Composer</h2>
        <div className='flex items-center gap-3'>
          <button
            className='px-3 py-2 rounded-lg border border-border bg-white text-sm md:hidden hover:bg-muted'
            onClick={onOpenPreview}
          >
            Preview
          </button>
        </div>
      </div>
      <div className='p-4 space-y-4'>
        <div className='space-y-2'>
          <div className='flex items-center justify-between'>
            <label className='text-xs font-semibold text-muted-foreground'>Message</label>
            <div className='flex flex-col items-end gap-1'>
              <div className='flex items-center gap-2 text-xs'>
                <span className='text-muted-foreground'>Langue</span>
                <select
                  className={cn(
                    'rounded-lg border bg-white px-2 py-1 text-xs focus:outline-hidden focus:ring-2 focus:ring-foreground',
                    hasLangError ? 'border-red-500' : 'border-border'
                  )}
                  {...register('activeLang')}
                >
                  <option value={MessageLanguageEnum.FR}>{MessageLanguageEnum.FR}</option>
                  <option value={MessageLanguageEnum.EN}>{MessageLanguageEnum.EN}</option>
                </select>
              </div>
              {hasLangError && (
                <span className='flex items-center gap-1 text-xs text-red-500'>
                  <AlertCircle className='h-3 w-3' />
                  Champs {activeLang === MessageLanguageEnum.FR ? 'FR' : 'EN'} incomplets
                </span>
              )}
            </div>
          </div>
          <textarea
            ref={setFrRef}
            className={`mt-2 w-full h-32 rounded-xl border px-3 py-2 text-sm focus:outline-hidden focus:ring-1 ${
              messageFrError ? 'border-destructive focus:ring-destructive' : 'border-foreground focus:ring-foreground'
            } ${activeLang === MessageLanguageEnum.FR ? '' : 'hidden'}`}
            placeholder='Bonjour…'
            {...messageFrRegister}
          />
          {activeLang === MessageLanguageEnum.FR && messageFrError && (
            <p className='text-xs text-destructive mt-1'>{messageFrError}</p>
          )}
          <textarea
            ref={setEnRef}
            className={`mt-2 w-full h-32 rounded-xl border px-3 py-2 text-sm focus:outline-hidden focus:ring-1 ${
              messageEnError ? 'border-destructive focus:ring-destructive' : 'border-foreground focus:ring-foreground'
            } ${activeLang === MessageLanguageEnum.EN ? '' : 'hidden'}`}
            placeholder='Hello…'
            {...messageEnRegister}
          />
          {activeLang === MessageLanguageEnum.EN && messageEnError && (
            <p className='text-xs text-destructive mt-1'>{messageEnError}</p>
          )}

          {/* Toolbar: char count + insert chips */}
          <div className='flex items-center justify-between mt-1'>
            <div className='flex items-center gap-1.5'>
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className='h-3.5 w-3.5 text-muted-foreground cursor-help shrink-0' />
                  </TooltipTrigger>
                  <TooltipContent side='top' className='max-w-[260px] text-xs space-y-1.5'>
                    <p className='font-semibold text-foreground mb-1'>Variables disponibles</p>
                    <p><span className='font-mono bg-muted px-1 rounded'>{'{{ username }}'}</span> — prénom de l'utilisateur</p>
                    <p><span className='font-mono bg-muted px-1 rounded'>{'{{ action }}'}</span> — texte du bouton CTA (si renseigné)</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <button
                type='button'
                onClick={handleInsertUsername}
                className='inline-flex items-center gap-1 rounded-md border border-border bg-muted/40 px-2 py-0.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground transition-colors'
              >
                <span className='font-mono'>{'{{ username }}'}</span>
              </button>
              <button
                type='button'
                onClick={handleInsertAction}
                className='inline-flex items-center gap-1 rounded-md border border-border bg-muted/40 px-2 py-0.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground transition-colors'
              >
                <span className='font-mono'>{'{{ action }}'}</span>
              </button>
            </div>
            <span className='text-xs text-muted-foreground'>
              {(activeLang === MessageLanguageEnum.FR ? messageFr : messageEn).length} / 500
            </span>
          </div>
        </div>

        {/* CTA section */}
        <div className='rounded-xl border border-border px-3 py-2.5 space-y-2'>
          <p className='text-xs font-semibold text-muted-foreground'>Action (CTA) (optionnel)</p>
          <div className='grid grid-cols-2 gap-2'>
            <div className='space-y-1'>
              <label className='text-xs text-muted-foreground'>Texte FR</label>
              <input
                type='text'
                placeholder='Ex : ici'
                className={inputClass}
                {...register('cta.text.fr')}
              />
              {activeLang === MessageLanguageEnum.FR && errors.cta?.text?.fr && (
                <span className='text-xs text-red-500'>{errors.cta.text.fr.message}</span>
              )}
            </div>
            <div className='space-y-1'>
              <label className='text-xs text-muted-foreground'>Texte EN</label>
              <input
                type='text'
                placeholder='Ex : here'
                className={inputClass}
                {...register('cta.text.en')}
              />
              {activeLang === MessageLanguageEnum.EN && errors.cta?.text?.en && (
                <span className='text-xs text-red-500'>{errors.cta.text.en.message}</span>
              )}
            </div>
          </div>
          <ControlledSelect
            name='cta.appPath'
            control={control}
            placeholder='Naviguer vers…'
            options={APP_SCREEN_OPTIONS}
            clearable
          />
        </div>

        <div className='grid grid-cols-1 md:hidden gap-3'>
          <div className='flex flex-col gap-1.5'>
            <label className='text-xs font-semibold text-muted-foreground'>Audience</label>
            <ControlledSelect
              control={control}
              name='audience'
              options={audienceOptions}
              placeholder='Sélectionner une audience'
              className='bg-muted/30 focus:bg-background transition-colors h-9'
            />
          </div>
        </div>

        {audience === MessageAudienceEnum.SPECIFIC && (
          <div className='md:hidden'>
            <UserMultiSelect />
          </div>
        )}

        <div className='flex items-center justify-end'>
          <div className='flex items-center gap-2'>
            <button
              className='px-4 py-2 rounded-lg bg-foreground text-background text-sm shadow-soft hover:bg-foreground/90'
              onClick={onSend}
              type='button'
            >
              Envoyer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComposerCard;
